import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Search,
  RefreshCw,
  LogOut,
  Mail,
  Inbox,
  Users,
  Trash2,
  X,
  Phone,
  MapPin,
  Calendar,
  CarFront,
  FileText,
  Hash,
  Link as LinkIcon,
  Copy,
  DollarSign,
  CheckCheck,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUS_LABELS = {
  new: { label: "New", cls: "bg-[#0055FF] text-white" },
  contacted: { label: "Contacted", cls: "bg-amber-500 text-white" },
  closed: { label: "Closed", cls: "bg-slate-300 text-slate-700" },
};

const SOURCE_LABELS = {
  email: "Email Inbox",
  contact_form: "Contact Form",
};

export default function AdminDashboard() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [fetching, setFetching] = useState(false);
  const navigate = useNavigate();

  const loadLeads = async () => {
    setLoading(true);
    try {
      const res = await api.get("/leads");
      setLeads(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("mrc_token");
        navigate("/admin");
      }
      toast.error("Could not load leads");
    } finally {
      setLoading(false);
    }
  };

  const loadEmailStatus = async () => {
    try {
      const res = await api.get("/email/status");
      setEmailStatus(res.data);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    loadLeads();
    loadEmailStatus();
  }, []);

  const fetchEmails = async () => {
    setFetching(true);
    try {
      const res = await api.post("/email/fetch");
      if (res.data.configured) {
        toast.success(res.data.message || `Imported ${res.data.imported}`);
        await loadLeads();
      } else {
        toast.message(res.data.message || "IMAP not configured");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fetch failed");
    } finally {
      setFetching(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}`, { status });
      setLeads((arr) => arr.map((l) => (l.id === id ? { ...l, status } : l)));
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch {
      toast.error("Update failed");
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await api.delete(`/leads/${id}`);
      setLeads((arr) => arr.filter((l) => l.id !== id));
      setSelected(null);
      toast.success("Lead deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const logout = () => {
    localStorage.removeItem("mrc_token");
    navigate("/admin");
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return leads.filter((l) => {
      if (filter !== "all" && l.status !== filter) return false;
      if (!q) return true;
      const blob = [
        l.contact_name,
        l.contact_phone,
        l.contact_email,
        l.car_type,
        l.car_plate,
        l.address,
        l.notes,
        l.raw_subject,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return blob.includes(q);
    });
  }, [leads, query, filter]);

  const stats = useMemo(() => {
    const total = leads.length;
    const newCount = leads.filter((l) => l.status === "new").length;
    const fromEmail = leads.filter((l) => l.source === "email").length;
    const fromForm = leads.filter((l) => l.source === "contact_form").length;
    return { total, newCount, fromEmail, fromForm };
  }, [leads]);

  return (
    <div className="min-h-screen bg-[#F4F5F8]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/logo.jpg"
              alt="MR Crusher logo"
              className="h-10 w-10 object-contain"
            />
            <span className="font-display font-bold uppercase tracking-tight">
              MR Crusher
            </span>
            <span className="ml-3 text-xs uppercase tracking-widest text-slate-500 font-mono-plex hidden sm:inline">
              / control room
            </span>
          </Link>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#0055FF] transition-colors"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#0055FF]">
              Lead Intake
            </div>
            <h1 className="mt-2 font-display font-semibold text-3xl md:text-4xl tracking-tight">
              Extracted leads
            </h1>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={loadLeads}
              data-testid="refresh-leads"
              className="inline-flex items-center gap-2 border border-slate-300 bg-white hover:bg-slate-50 px-4 py-2.5 text-sm font-medium transition-colors"
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button
              onClick={fetchEmails}
              disabled={fetching}
              data-testid="fetch-emails"
              className="inline-flex items-center gap-2 bg-[#0055FF] hover:bg-[#003ECE] disabled:opacity-60 text-white px-4 py-2.5 text-sm font-medium transition-colors"
            >
              <Mail className="h-4 w-4" />
              {fetching ? "Fetching..." : "Fetch new emails"}
            </button>
          </div>
        </div>

        {emailStatus && !emailStatus.configured && (
          <div
            className="mt-6 border border-amber-300 bg-amber-50 text-amber-900 px-5 py-4 text-sm flex items-start gap-3"
            data-testid="imap-warning"
          >
            <Mail className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <strong>IMAP inbox not connected.</strong> Showing seeded demo
              data + contact-form leads. Add{" "}
              <code className="font-mono-plex">IMAP_HOST</code>,{" "}
              <code className="font-mono-plex">IMAP_USER</code>, and{" "}
              <code className="font-mono-plex">IMAP_PASSWORD</code> to{" "}
              <code className="font-mono-plex">backend/.env</code> to start
              syncing real emails sent to{" "}
              <strong>{emailStatus.destination_email || "your inbox"}</strong>.
            </div>
          </div>
        )}

        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total leads" value={stats.total} icon={<Users className="h-4 w-4" />} />
          <StatCard label="New" value={stats.newCount} icon={<Inbox className="h-4 w-4" />} accent />
          <StatCard label="From email" value={stats.fromEmail} icon={<Mail className="h-4 w-4" />} />
          <StatCard label="From form" value={stats.fromForm} icon={<FileText className="h-4 w-4" />} />
        </div>

        <div className="mt-8 bg-white border border-slate-200">
          <div className="border-b border-slate-200 p-4 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, plate, car, address..."
                data-testid="search-leads"
                className="w-full pl-10 pr-4 py-2.5 border border-slate-300 bg-white text-sm focus:outline-none focus:ring-1 focus:ring-[#0055FF] focus:border-[#0055FF]"
              />
            </div>
            <div className="flex gap-1 border border-slate-300">
              {["all", "new", "contacted", "closed"].map((s) => (
                <button
                  key={s}
                  onClick={() => setFilter(s)}
                  data-testid={`filter-${s}`}
                  className={`px-4 py-2 text-xs uppercase tracking-widest font-medium transition-colors ${
                    filter === s
                      ? "bg-slate-900 text-white"
                      : "bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="p-16 text-center text-slate-500">Loading leads...</div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center text-slate-500" data-testid="empty-leads">
              No leads match your filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="leads-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-xs uppercase tracking-widest text-slate-500">
                    <th className="px-5 py-3 font-medium">Source</th>
                    <th className="px-5 py-3 font-medium">Contact</th>
                    <th className="px-5 py-3 font-medium">Vehicle</th>
                    <th className="px-5 py-3 font-medium">Plate</th>
                    <th className="px-5 py-3 font-medium">Address</th>
                    <th className="px-5 py-3 font-medium">Status</th>
                    <th className="px-5 py-3 font-medium">Received</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l) => (
                    <tr
                      key={l.id}
                      onClick={() => setSelected(l)}
                      className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                      data-testid={`lead-row-${l.id}`}
                    >
                      <td className="px-5 py-4">
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600">
                          {l.source === "email" ? (
                            <Mail className="h-3.5 w-3.5 text-[#0055FF]" />
                          ) : (
                            <FileText className="h-3.5 w-3.5 text-emerald-600" />
                          )}
                          {SOURCE_LABELS[l.source]}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="font-medium text-slate-900">
                          {l.contact_name || "—"}
                        </div>
                        <div className="text-xs text-slate-500 font-mono-plex">
                          {l.contact_phone || l.contact_email || "—"}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="font-medium">{l.car_type || "—"}</div>
                        <div className="text-xs text-slate-500 font-mono-plex">
                          {l.car_year || ""}
                        </div>
                      </td>
                      <td className="px-5 py-4 font-mono-plex text-xs">
                        {l.car_plate || "—"}
                      </td>
                      <td className="px-5 py-4 text-slate-600 max-w-xs truncate">
                        {l.address || "—"}
                      </td>
                      <td className="px-5 py-4">
                        <StatusBadge status={l.status} />
                      </td>
                      <td className="px-5 py-4 text-xs text-slate-500 font-mono-plex">
                        {formatDate(l.created_at)}
                      </td>
                      <td className="px-5 py-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteLead(l.id);
                          }}
                          data-testid={`delete-${l.id}`}
                          className="text-slate-400 hover:text-red-600 transition-colors"
                          aria-label="Delete lead"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {selected && (
        <LeadDrawer
          lead={selected}
          onClose={() => setSelected(null)}
          onStatus={(s) => setStatus(selected.id, s)}
          onDelete={() => deleteLead(selected.id)}
          onGenerateLink={async (offer) => {
            try {
              const res = await api.post(
                `/leads/${selected.id}/appointment-link`,
                { offer_amount: Number(offer) },
              );
              const updated = { ...selected, offer_amount: Number(offer), appointment_token: res.data.token };
              setSelected(updated);
              setLeads((arr) => arr.map((l) => (l.id === selected.id ? updated : l)));
              toast.success("Appointment link generated");
              return res.data;
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Couldn't generate link");
              throw err;
            }
          }}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, icon, accent }) {
  return (
    <div
      className={`bg-white border border-slate-200 p-5 ${accent ? "border-l-4 border-l-[#0055FF]" : ""}`}
      data-testid={`stat-${label.toLowerCase().replace(" ", "-")}`}
    >
      <div className="flex items-center justify-between text-xs uppercase tracking-widest text-slate-500">
        <span>{label}</span>
        {icon}
      </div>
      <div className="mt-3 font-display font-semibold text-3xl tracking-tight text-slate-900">
        {value}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_LABELS[status] || STATUS_LABELS.new;
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 text-xs font-medium uppercase tracking-wider ${s.cls}`}
    >
      {s.label}
    </span>
  );
}

function LeadDrawer({ lead, onClose, onStatus, onDelete, onGenerateLink }) {
  const [offer, setOffer] = useState(
    lead.offer_amount != null ? String(lead.offer_amount) : "",
  );
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const bookingUrl = lead.appointment_token
    ? `${window.location.origin}/date/${lead.appointment_token}`
    : "";

  const handleGenerate = async () => {
    if (!offer || Number(offer) < 0) {
      toast.error("Enter a valid offer amount.");
      return;
    }
    setGenerating(true);
    try {
      await onGenerateLink(offer);
    } finally {
      setGenerating(false);
    }
  };

  const copy = async () => {
    if (!bookingUrl) return;
    try {
      await navigator.clipboard.writeText(bookingUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — long-press to copy manually.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      data-testid="lead-drawer"
    >
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-xl bg-white h-full overflow-y-auto border-l border-slate-200">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#0055FF]">
            Lead Detail
          </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-900"
            data-testid="close-drawer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 md:p-8 space-y-6">
          <div>
            <div className="font-display font-semibold text-2xl tracking-tight">
              {lead.contact_name || "Unnamed lead"}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <StatusBadge status={lead.status} />
              <span className="text-xs uppercase tracking-widest text-slate-500">
                {SOURCE_LABELS[lead.source]}
              </span>
              <span className="text-xs text-slate-500 font-mono-plex">
                · {formatDate(lead.created_at)}
              </span>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Detail icon={<Phone />} label="Phone" value={lead.contact_phone} />
            <Detail icon={<Mail />} label="Email" value={lead.contact_email} />
            <Detail icon={<CarFront />} label="Vehicle" value={lead.car_type} />
            <Detail icon={<Calendar />} label="Year" value={lead.car_year} />
            <Detail icon={<Hash />} label="Plate" value={lead.car_plate} />
            <Detail icon={<MapPin />} label="Postal / Address" value={lead.postal_code || lead.address} />
            <Detail icon={<Hash />} label="Mileage" value={lead.mileage} />
            <Detail icon={<FileText />} label="Has registration" value={lead.has_registration} />
            <Detail icon={<CarFront />} label="Tires" value={lead.tires} />
            <Detail icon={<CarFront />} label="Wheels" value={lead.wheels} />
            <Detail icon={<CarFront />} label="Condition" value={lead.condition} />
            <Detail icon={<CarFront />} label="Transmission" value={lead.transmission} />
            <Detail icon={<CarFront />} label="Runs" value={lead.runs} />
            <Detail icon={<CarFront />} label="Rust / dents" value={lead.damage} />
            <Detail icon={<CarFront />} label="Missing parts" value={lead.missing_parts} />
          </div>

          {lead.notes && (
            <div>
              <div className="text-xs font-medium uppercase tracking-widest text-slate-500 mb-2">
                Notes
              </div>
              <div className="border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 whitespace-pre-wrap">
                {lead.notes}
              </div>
            </div>
          )}

          {lead.raw_subject && (
            <div>
              <div className="text-xs font-medium uppercase tracking-widest text-slate-500 mb-2">
                Email subject
              </div>
              <div className="font-mono-plex text-sm text-slate-700">
                {lead.raw_subject}
              </div>
            </div>
          )}

          {lead.raw_body && (
            <div>
              <div className="text-xs font-medium uppercase tracking-widest text-slate-500 mb-2">
                Original email
              </div>
              <pre className="border border-slate-200 bg-slate-50 p-4 text-xs text-slate-700 whitespace-pre-wrap overflow-x-auto max-h-64 font-mono-plex">
                {lead.raw_body}
              </pre>
            </div>
          )}

          <div className="border-t border-slate-200 pt-6">
            <div className="text-xs font-medium uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-[#0055FF]" />
              Cash offer &amp; pickup link
            </div>
            <div className="flex items-stretch gap-2">
              <div className="flex-1 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={offer}
                  onChange={(e) => setOffer(e.target.value)}
                  placeholder="e.g. 450"
                  data-testid="drawer-offer-input"
                  className="w-full pl-7 pr-3 py-2.5 border border-slate-300 text-sm focus:outline-none focus:ring-1 focus:ring-[#0055FF] focus:border-[#0055FF]"
                />
              </div>
              <button
                onClick={handleGenerate}
                disabled={generating}
                data-testid="drawer-generate-link"
                className="inline-flex items-center gap-2 bg-[#0055FF] hover:bg-[#003ECE] disabled:opacity-60 text-white px-4 py-2.5 text-sm font-medium transition-colors"
              >
                <LinkIcon className="h-4 w-4" />
                {generating
                  ? "Generating…"
                  : lead.appointment_token
                    ? "Update"
                    : "Generate"}
              </button>
            </div>
            {bookingUrl && (
              <div
                className="mt-3 flex items-stretch gap-2"
                data-testid="drawer-booking-url"
              >
                <input
                  readOnly
                  value={bookingUrl}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 px-3 py-2.5 border border-slate-300 bg-slate-50 text-xs font-mono-plex text-slate-700"
                />
                <button
                  onClick={copy}
                  data-testid="drawer-copy-link"
                  className="inline-flex items-center gap-2 border border-slate-300 hover:bg-slate-50 px-3 py-2.5 text-xs font-medium transition-colors"
                >
                  {copied ? <CheckCheck className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
            )}
            {lead.appointment_status === "scheduled" && (
              <div className="mt-3 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2">
                Scheduled: <strong>{lead.appointment_date}</strong> · {lead.appointment_time}
              </div>
            )}
          </div>

          <div className="border-t border-slate-200 pt-6">
            <div className="text-xs font-medium uppercase tracking-widest text-slate-500 mb-3">
              Update status
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.keys(STATUS_LABELS).map((s) => (
                <button
                  key={s}
                  onClick={() => onStatus(s)}
                  data-testid={`set-status-${s}`}
                  className={`px-4 py-2 text-xs uppercase tracking-widest font-medium border transition-colors ${
                    lead.status === s
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  {STATUS_LABELS[s].label}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={onDelete}
            data-testid="delete-from-drawer"
            className="inline-flex items-center gap-2 text-sm text-red-600 hover:text-red-800 transition-colors"
          >
            <Trash2 className="h-4 w-4" /> Delete this lead
          </button>
        </div>
      </div>
    </div>
  );
}

function Detail({ icon, label, value }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-widest text-slate-500 mb-1.5 flex items-center gap-1.5">
        <span className="text-[#0055FF]">{icon}</span>
        {label}
      </div>
      <div className="text-sm text-slate-900 font-medium">
        {value || <span className="text-slate-400">Not provided</span>}
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
